/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { LanguageCard } from 'src/languageStudio/appShell/prebuiltDemos/components/LanguageCard';
import { demoTheme, resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { Conversation, ConversationItem, SummaryAspects } from 'src/languageStudio/core/services/conversation/conversationSwaggers';
import { DetectedLanguagesDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { AnalyzedSentence } from 'src/packages/analyzed-conversation-viewer/lib/esm';
import styled from 'styled-components';

type VisualizedConversationSummarizationResultProps = {
    isRTL: boolean;
    conversationItems: ConversationItem[];
    detectedLanguage: DetectedLanguagesDocuments;
    conversationSummarizationResult: Conversation;
};

const messages = defineMessages({
    issue: { id: 'languageStudio.visualizedConversationSummarizationResult.issue', defaultMessage: 'Issue' },
    narrative: { id: 'languageStudio.visualizedConversationSummarizationResult.narrative', defaultMessage: 'Narrative' },
    resolution: { id: 'languageStudio.visualizedConversationSummarizationResult.resolution', defaultMessage: 'Resolution' },
    title: { id: 'languageStudio.visualizedConversationSummarizationResult.title', defaultMessage: 'Conversation Summarization' },
    chapterTitle: { id: 'languageStudio.visualizedConversationSummarizationResult.chapterTitle', defaultMessage: 'Chapter title' },
    recap: { id: 'languageStudio.visualizedConversationSummarizationResult.recap', defaultMessage: 'Recap' },
    'follow-up tasks': {
        id: 'languageStudio.visualizedConversationSummarizationResult.followUpTasks',
        defaultMessage: 'Follow-up Tasks (Preview)'
    }
});

const CardContentWrapper = styled.ul({ paddingLeft: '20px' });

export const VisualizedConversationSummarizationResult = (props: VisualizedConversationSummarizationResultProps) => {
    const { detectedLanguage, conversationItems, conversationSummarizationResult } = props;
    const { localize } = useLocalization();
    const aspects: SummaryAspects[] = ['recap', 'issue', 'resolution', 'follow-up tasks', 'chapterTitle', 'narrative'];
    const aspectsToDisplay = aspects
        .map(aspect => {
            const contextArr = [];
            const textArr = [];
            for (const cs of conversationSummarizationResult?.summaries) {
                if (cs.aspect !== aspect) {
                    continue;
                }
                if (cs.contexts?.length) {
                    contextArr.push(...cs.contexts);
                }
                if (cs.text) {
                    textArr.push(cs.text);
                }
            }

            return textArr.length ? { aspect, context: contextArr, text: textArr.join('|') } : null;
        })
        .filter(Boolean);

    return (
        <Stack styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 25 }}>
            <LuisText fontWeight={500}>{localize(messages.title)}</LuisText>

            {aspectsToDisplay.map((summary, summaryIndex) => (
                <Stack key={summaryIndex} tokens={{ childrenGap: 7 }} styles={resultCardStyles}>
                    <LuisText fontWeight={500} styles={resultCardTitleStyles(demoTheme.primaryColor)}>
                        {localize(messages[summary.aspect])}
                    </LuisText>

                    <CardContentWrapper>
                        {summary.text
                            .split('|')
                            .map(t => t.trim())
                            .filter(Boolean)
                            .map(bullet => (
                                <li>
                                    <LuisText>{bullet}</LuisText>
                                </li>
                            ))}
                    </CardContentWrapper>
                </Stack>
            ))}

            <LanguageCard language={detectedLanguage?.documents[0].detectedLanguage} />

            <Stack tokens={{ maxHeight: '450px' }}>
                {conversationItems.map(item => (
                    <AnalyzedSentence
                        hidePii={false}
                        showSentiment={false}
                        sentence={{ redactedText: item.text, speakerTitle: item.participantId, text: item.text }}
                    />
                ))}
            </Stack>
        </Stack>
    );
};
